import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import type { EventStatus, EventType } from "@/types/domain";

// ─── Filter / UI state local to this slice ───────────────────────
export interface EventFilters {
  status: EventStatus | "all";
  type: EventType | "all";
  search: string;
  dateFrom: string | null;
  dateTo: string | null;
}

// ─── State shape ──────────────────────────────────────────────────
export interface EventsState {
  /** IDs of events currently visible / selected in the UI */
  selectedEventIds: string[];
  /** Active filters in the events list view */
  filters: EventFilters;
  /** Track which event detail panels are open (for split-view) */
  openEventId: string | null;
  isLoading: boolean;
  error: string | null;
  currentEvent: any;
  platformEventMembers: any;
  eventContents: any;
}

const defaultFilters: EventFilters = {
  status: "all",
  type: "all",
  search: "",
  dateFrom: null,
  dateTo: null,
};

const initialState: EventsState = {
  selectedEventIds: [],
  filters: defaultFilters,
  openEventId: null,
  isLoading: false,
  error: null,
  currentEvent: null,
  platformEventMembers: null,
  eventContents: null,
};

// ─── Slice ────────────────────────────────────────────────────────
const eventsSlice = createSlice({
  name: "events",
  initialState,
  reducers: {
    setFilters(state, action: PayloadAction<Partial<EventFilters>>) {
      state.filters = { ...state.filters, ...action.payload };
    },

    resetFilters(state) {
      state.filters = defaultFilters;
    },

    setOpenEvent(state, action: PayloadAction<string | null>) {
      state.openEventId = action.payload;
    },

    selectEvent(state, action: PayloadAction<string>) {
      if (!state.selectedEventIds.includes(action.payload)) {
        state.selectedEventIds.push(action.payload);
      }
    },

    deselectEvent(state, action: PayloadAction<string>) {
      state.selectedEventIds = state.selectedEventIds.filter(
        (id) => id !== action.payload,
      );
    },

    clearSelection(state) {
      state.selectedEventIds = [];
    },

    setLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },

    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
      state.isLoading = false;
    },

    setCurrentEvent(state, action: PayloadAction<any>) {
      state.currentEvent = action.payload;
    },

    clearCurrentEvent(state) {
      state.currentEvent = null;
    },

    setPlatformEventMembers(state, action: PayloadAction<any>) {
      state.platformEventMembers = action.payload;
    },

    clearPlatformEventMembers(state) {
      state.platformEventMembers = null;
    },

    setEventContents(state, action: PayloadAction<any>) {
      state.eventContents = action.payload;
    },

    clearEventContents(state) {
      state.eventContents = null;
    },
  },
});

export const {
  setFilters,
  resetFilters,
  setOpenEvent,
  selectEvent,
  deselectEvent,
  clearSelection,
  setLoading,
  setError,
  setCurrentEvent,
  clearCurrentEvent,
  setPlatformEventMembers,
  clearPlatformEventMembers,
  setEventContents,
  clearEventContents,
} = eventsSlice.actions;

export default eventsSlice.reducer;
